import React, { useEffect, useState } from 'react'
import { CheckIcon, InfoIcon } from 'lucide-react'
import { supabase } from '../lib/supabase'
import type { SelectedContractLineItem } from '../types/job'
import { formatUnitTypeLabel } from '../lib/unitTypeHelpers'

interface ContractLineItemRow {
  id: string
  contract_service_id?: string | null
  service_item_id?: string | null
  description: string
  quantity: number
  is_variable: boolean
  unit_type: string
  unit_price: number
  total_price: number
  sort_order: number
}

interface ContractLineItemPickerProps {
  contractId: string
  onSelectionChange: (items: SelectedContractLineItem[], total: number) => void
  onAvailableCountChange?: (count: number) => void
}

export function ContractLineItemPicker({
  contractId,
  onSelectionChange,
  onAvailableCountChange,
}: ContractLineItemPickerProps) {
  const [lineItems, setLineItems] = useState<ContractLineItemRow[]>([])
  const [selected, setSelected] = useState<Map<string, SelectedContractLineItem>>(new Map())
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!contractId) return
    const load = async () => {
      setLoading(true)
      setError(null)
      try {
        const { data, error: fetchError } = await supabase
          .from('contract_line_items')
          .select(
            'id, contract_service_id, service_item_id, description, quantity, is_variable, unit_type, unit_price, total_price, sort_order'
          )
          .eq('contract_id', contractId)
          .order('sort_order', { ascending: true })

        if (fetchError) throw fetchError
        const rows = data || []
        setLineItems(rows)
        onAvailableCountChange?.(rows.length)
      } catch (err) {
        setError((err as Error).message)
        onAvailableCountChange?.(0)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [contractId, onAvailableCountChange])

  useEffect(() => {
    const items = Array.from(selected.values())
    const total = items.reduce((sum, item) => sum + item.total_price, 0)
    onSelectionChange(items, total)
  }, [selected, onSelectionChange])

  const toggleItem = (row: ContractLineItemRow) => {
    setSelected((prev) => {
      const next = new Map(prev)
      if (next.has(row.id)) {
        next.delete(row.id)
      } else {
        next.set(row.id, {
          contract_line_item_id: row.id,
          contract_service_id: row.contract_service_id,
          service_item_id: row.service_item_id,
          description: row.description,
          quantity: row.is_variable ? 1 : row.quantity || 1,
          is_variable: row.is_variable,
          unit_type: row.unit_type,
          unit_price: row.unit_price,
          total_price: row.is_variable ? row.unit_price : (row.quantity || 1) * row.unit_price,
        })
      }
      return next
    })
  }

  const updateQuantity = (rowId: string, qty: number) => {
    setSelected((prev) => {
      const item = prev.get(rowId)
      if (!item || item.is_variable) return prev
      const next = new Map(prev)
      const quantity = Math.max(0, qty)
      next.set(rowId, {
        ...item,
        quantity,
        total_price: quantity * item.unit_price,
      })
      return next
    })
  }

  if (loading) {
    return <p className="text-sm text-gray-500">Loading contract line items...</p>
  }

  if (error) {
    return <p className="text-sm text-red-600">{error}</p>
  }

  if (lineItems.length === 0) {
    return (
      <p className="text-sm text-gray-500">
        No line items on this contract. Add rate card tiers on the contract billing tab first.
      </p>
    )
  }

  return (
    <div className="space-y-3">
      <div className="flex items-start gap-2 p-3 bg-indigo-50 border border-indigo-100 rounded-lg text-sm text-indigo-800">
        <InfoIcon className="w-4 h-4 mt-0.5 flex-shrink-0" />
        <span>
          Select unit sizes or services from the contract rate card. Quantities determine what this
          job will bill.
        </span>
      </div>
      {lineItems.map((row) => {
        const isSelected = selected.has(row.id)
        const selectedItem = selected.get(row.id)
        return (
          <div
            key={row.id}
            className={`border rounded-lg p-4 ${isSelected ? 'border-indigo-300 bg-indigo-50' : 'border-gray-200 bg-white'}`}
          >
            <div className="flex items-start gap-3">
              <button
                type="button"
                onClick={() => toggleItem(row)}
                className={`mt-1 w-5 h-5 rounded border flex items-center justify-center ${
                  isSelected ? 'bg-indigo-600 border-indigo-600 text-white' : 'border-gray-300'
                }`}
              >
                {isSelected && <CheckIcon className="w-3 h-3" />}
              </button>
              <div className="flex-1">
                <div className="flex justify-between gap-4">
                  <div>
                    <p className="font-medium text-gray-900">{row.description}</p>
                    <p className="text-sm text-gray-500">
                      ${row.unit_price.toFixed(2)} / {formatUnitTypeLabel(row.unit_type)}
                      {row.is_variable && ' (variable)'}
                    </p>
                  </div>
                  {isSelected && selectedItem && (
                    <div className="text-right">
                      <p className="text-sm font-semibold text-gray-900">
                        ${selectedItem.total_price.toFixed(2)}
                      </p>
                    </div>
                  )}
                </div>
                {isSelected && !row.is_variable && (
                  <div className="mt-3 max-w-xs">
                    <label className="block text-xs font-medium text-gray-600 mb-1">Quantity</label>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={selectedItem?.quantity ?? 1}
                      onChange={(e) => updateQuantity(row.id, parseFloat(e.target.value) || 0)}
                      className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                )}
                {isSelected && row.is_variable && (
                  <p className="mt-2 text-xs text-orange-600">
                    Quantity will be confirmed at job completion.
                  </p>
                )}
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}
